/* ============================================================
   TASK-298 — "two-track" plan presentation: Track 1 (Scheduler, publish-
   only, per-connected-social, no AI) vs Track 2 (AI content plans:
   starter/growth/pro/agency). Pure module — no React, no fetch — so it is
   testable under plain vitest (see planTracks.test.js).

   This is the single "all plans in display order" source the TASK-298
   audit found missing: today (pre-this-task) Plans.jsx, PlanSelectScreen.jsx
   and TrialCardCapture.jsx each spliced the Scheduler entry into their own
   local array independently, producing different card counts/orders and
   the B4 "stranded card" grid-overflow bug. Callers pass in their own
   getNewPlanDisplay/getPostingPlanDisplay/tx/t so this module never talks
   to the network or the DOM.
   ============================================================ */
import { NEW_PLAN_DEFS, NEW_PLAN_TEXT_KEYS } from './plans.js';
import { buildPostingPriceLine, countConnectedSocials, POSTING_UNIT_PRICE_CENTS_DEFAULT } from './postingPlan.js';

// The Track-2 grid is always these three tiers; Agency is the "See all
// plans" extra (Layouts §/app/plans and §picker, both surfaces).
const AI_TRACK_IDS = ['starter', 'growth', 'pro'];
const EXTRA_TRACK_IDS = ['agency'];

// Hardcoded price-cents fallback for the new grid's price COMPARISON logic
// (resolveSwitchLabel) — mirrors the priceFormatted strings on NEW_PLAN_DEFS
// (lib/plans.js) in cents, used only when the DB catalog hasn't supplied a
// priceCents field for that plan yet (see getNewPlanDisplay's own
// per-field hardcoded-fallback convention in hooks/usePlanCatalog.js).
const NEW_PLAN_PRICE_CENTS_FALLBACK = {
  starter: 1900,
  growth: 4900,
  pro: 9900,
  agency: 24900,
};

/**
 * Grid column count from the rendered AI-card count — the count<->columns
 * invariant that makes the B4 "stranded card" bug structurally impossible:
 * both /app/plans and the post-onboarding picker derive their grid's column
 * count from this instead of a hardcoded number.
 */
export function gridColsClass(count) {
  return count === 4 ? 'cols-4' : 'cols-3';
}

/**
 * Which CTA wording a non-current plan card/band should use, given its
 * price vs. the current live plan's price (in cents). 'switch' covers the
 * equal-price and either-price-unknown cases (plansPage.switchPlan).
 */
export function resolveSwitchLabel({ targetPriceCents, currentPriceCents }) {
  if (!Number.isFinite(targetPriceCents) || !Number.isFinite(currentPriceCents)) return 'switch';
  if (targetPriceCents > currentPriceCents) return 'upgrade';
  if (targetPriceCents < currentPriceCents) return 'downgrade';
  return 'switch';
}

/**
 * The Scheduler band's live-total copy key, picked in JS (not via i18next
 * plural suffixes — see postingPlan.liveTotal's guarded-block note: ru/uk/
 * pl/cs/lt/lv need different plural categories than en, and a _one/_other
 * suffix would break the locales.test.js exact-key-set parity check).
 *
 * quantity is the RAW connected-social count (not floored to a billing
 * minimum of 1) so 0/unknown renders postingGate.quantityZero instead of a
 * fabricated "0 connected accounts = €0/month".
 */
export function buildLiveTotalCopy({ quantity, totalFormatted, t }) {
  const translate = typeof t === 'function' ? t : (key) => key;
  if (!Number.isFinite(quantity) || quantity <= 0) {
    return translate('postingGate.quantityZero');
  }
  if (quantity === 1) {
    return translate('postingPlan.liveTotalOne', { total: totalFormatted });
  }
  return translate('postingPlan.liveTotal', { quantity, total: totalFormatted });
}

function buildAiEntry(def, { getNewPlanDisplay, tx }) {
  const keys = NEW_PLAN_TEXT_KEYS[def.id] || {};
  const display = typeof getNewPlanDisplay === 'function' ? (getNewPlanDisplay(def.id) || {}) : {};
  const priceCents = typeof display.priceCents === 'number'
    ? display.priceCents
    : (NEW_PLAN_PRICE_CENTS_FALLBACK[def.id] ?? null);
  const resolveTx = typeof tx === 'function' ? tx : (key, fallback) => fallback;

  // Pre-translated flat shape — what a simple radio-card list (the picker)
  // needs. Callers that render the richer card (badges/struck price/promo,
  // e.g. Plans.jsx) can ignore `.translated` and keep resolving `keys`/
  // `display` from `.id` themselves exactly as before; nothing here forces
  // a render-logic change on that side.
  const translated = {
    id: def.id,
    featured: !!def.featured,
    name: resolveTx(keys.name, display.name || def.name),
    description: resolveTx(keys.desc, display.description || def.desc),
    price: display.priceFormatted || def.price,
    unit: resolveTx(keys.priceUnit, display.unit || def.priceUnit),
    features: (def.feats || []).map((feature, index) => resolveTx(keys.feats?.[index], feature)),
  };

  return { ...def, keys, display, priceCents, translated };
}

/**
 * Builds the single "all plans in display order" source for a plan-
 * selection surface: the optional Scheduler (Track 1) entry, plus the
 * three-tier AI grid (Track 2) and the Agency "See all plans" extra.
 * 'posting' NEVER appears in aiTrack/extraTrack — only in publishTrack.
 */
export function buildPlanTracks({ getNewPlanDisplay, getPostingPlanDisplay, postingPlanActive, tx, t, connectedSocials } = {}) {
  const aiTrack = AI_TRACK_IDS
    .map((id) => NEW_PLAN_DEFS.find((p) => p.id === id))
    .filter(Boolean)
    .map((def) => buildAiEntry(def, { getNewPlanDisplay, tx }));

  const extraTrack = EXTRA_TRACK_IDS
    .map((id) => NEW_PLAN_DEFS.find((p) => p.id === id))
    .filter(Boolean)
    .map((def) => buildAiEntry(def, { getNewPlanDisplay, tx }));

  let publishTrack = null;
  if (postingPlanActive) {
    const postingDisplay = typeof getPostingPlanDisplay === 'function' ? (getPostingPlanDisplay() || {}) : {};
    const rawQuantity = countConnectedSocials(connectedSocials);
    const billedQuantity = Math.max(1, rawQuantity);
    const priceCents = typeof postingDisplay.priceCents === 'number'
      ? postingDisplay.priceCents
      : POSTING_UNIT_PRICE_CENTS_DEFAULT;
    const line = buildPostingPriceLine({
      quantity: billedQuantity,
      unitPriceCents: postingDisplay.priceCents ?? undefined,
      currency: postingDisplay.currency,
    });
    publishTrack = {
      id: 'posting',
      display: postingDisplay,
      priceCents,
      quantity: rawQuantity,
      billedQuantity,
      line,
      liveTotalCopy: buildLiveTotalCopy({ quantity: rawQuantity, totalFormatted: line.totalFormatted, t }),
    };
  }

  return { publishTrack, aiTrack, extraTrack };
}
